import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { purchasesApi } from '@/services/reparoly/purchasesApi';
import { setAppNotice } from '@/app/store/appShellSlice';
import type { AppDispatch } from '@/app/store/store';
import { setSelectedSupplierId } from '@/features/inventory/suppliers/suppliersSlice';

export type ReceiptLine = {
  id: string;
  inventory_id?: number | null;
  sku: string;
  name?: string;
  qty: number;
  unit_cost: number;
  serial_code?: string;
  serialized: boolean;
  ordered_qty?: number;
  received_qty?: number;
  pending_qty?: number;
};

const generateId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2));

type HeaderState = {
  purchaseOrderId: number | null;
  reference: string;
};

export type CurrentPO = { 
  id: number;
  label: string, 
  supplier_id: number, 
  supplier_name: string
};

type ReceiptEditorState = {
  header: HeaderState;
  lines: ReceiptLine[];
  currentPo: CurrentPO;
  status: 'idle' | 'resolving-po' | 'error';
};

export const initialStateCurrentPO: CurrentPO = {
  id: 0, 
  label: '',
  supplier_id: 0,
  supplier_name: ''
};

const initialState: ReceiptEditorState = {
  header: { purchaseOrderId: null, reference: '' },
  lines: [],
  currentPo: initialStateCurrentPO,
  status: 'idle',
};

const receiptEditorSlice = createSlice({
  name: 'receiptEditor',
  initialState,
  reducers: {
    reset: () => initialState,
    setPurchaseOrderId(state, action: PayloadAction<number | null>) {
      state.header.purchaseOrderId = action.payload ?? null;
    },
    setReference(state, action: PayloadAction<string>) {
      state.header.reference = action.payload ?? '';
    },
    setLines(state, action: PayloadAction<ReceiptLine[]>) {
      state.lines = Array.isArray(action.payload) ? action.payload : [];
    },
    addLine(state, action: PayloadAction<ReceiptLine>) {
      state.lines.push(action.payload);
    },
    updateLine(state, action: PayloadAction<{ id: string; patch: Partial<ReceiptLine> }>) {
      const { id, patch } = action.payload;
      state.lines = state.lines.map((l) => (l.id === id ? { ...l, ...patch } : l));
    },
    removeLine(state, action: PayloadAction<string>) {
      state.lines = state.lines.filter((l) => l.id !== action.payload);
    },
    duplicateLine(state, action: PayloadAction<string>) {
      const idx = state.lines.findIndex((l) => l.id === action.payload);
      if (idx >= 0) {
        const src = state.lines[idx];
        const clone: ReceiptLine = {
          ...src,
          id: generateId(),
          serial_code: src.serialized ? '' : src.serial_code,
        };
        state.lines.splice(idx + 1, 0, clone);
      }
    },
    setCurrentPo(state, action: PayloadAction<CurrentPO>) {
      state.currentPo = action.payload;
    },
    setStatus(state, action: PayloadAction<ReceiptEditorState['status']>) {
      state.status = action.payload;
    },
  },
});

export const {
  reset,
  setPurchaseOrderId,
  setReference,
  setLines,
  addLine,
  updateLine,
  removeLine,
  duplicateLine,
  setCurrentPo,
  setStatus,
} = receiptEditorSlice.actions;

export default receiptEditorSlice.reducer;

// Thunk: resolver PO por ID usando RTK Query (sin fetch manual)
export const resolvePOById = (id: number) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setStatus('resolving-po'));
    const res = await dispatch(purchasesApi.endpoints.getPODetail.initiate(id)).unwrap();
    const detailUnknown = res && typeof res === 'object' && 'data' in res ? (res as { data: unknown }).data : res;
    const detail = detailUnknown && typeof detailUnknown === 'object' ? (detailUnknown as Record<string, unknown>) : {};
    const supplierIdRaw = detail?.supplier_id ?? detail?.supplierId;
    const supplierId = typeof supplierIdRaw === 'number' ? supplierIdRaw : Number(supplierIdRaw ?? 0);
    // dispatch(setSelectedSupplierId(supplierId));
    const items = Array.isArray(detail?.items) ? (detail.items as unknown[]) : [];
    const mapped = items
      .map((item) => {
        const obj = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
        const skuRaw = obj?.sku ?? obj?.SKU;
        const sku = typeof skuRaw === 'string' ? skuRaw.trim() : '';
        const name = typeof obj?.name === 'string' ? obj.name : '';
        const inventoryId = obj?.inventory_id ?? obj?.inventoryId ?? null;
        if (!sku && inventoryId == null) return null;
        const orderedQtyNum = Number(obj?.ordered_qty ?? obj?.qty ?? obj?.quantity ?? 0);
        const receivedQtyNum = Number(obj?.received_qty ?? 0);
        const pendingQtyNum = Number(
          obj?.pending_qty ?? Math.max(orderedQtyNum - receivedQtyNum, 0),
        );
        const orderedQty = Number.isFinite(orderedQtyNum) ? orderedQtyNum : 0;
        const receivedQty = Number.isFinite(receivedQtyNum) ? receivedQtyNum : 0;
        const pendingQty = Number.isFinite(pendingQtyNum) ? pendingQtyNum : 0;
        const costRaw = obj?.unit_cost ?? obj?.unitCost ?? obj?.price ?? 0;
        const cost = Number(costRaw);
        const serialized = Boolean(obj?.serialized ?? obj?.track_serial);
        if (pendingQty <= 0) return null;
        return {
          id: generateId(),
          inventory_id: typeof inventoryId === 'number' ? inventoryId : null,
          sku,
          name,
          qty: serialized ? 1 : Math.max(1, pendingQty),
          unit_cost: Number.isFinite(cost) ? cost : 0,
          serial_code: '',
          serialized,
          ordered_qty: orderedQty,
          received_qty: receivedQty,
          pending_qty: pendingQty,
        } as ReceiptLine;
      })
      .filter((l): l is ReceiptLine => Boolean(l));
    dispatch(setLines(mapped));
    const supplierName = typeof detail?.supplier_name === 'string' ? detail.supplier_name : '';
    const label = `PO #${id}${supplierName ? ` · ${supplierName}` : ''}`;
    dispatch(setCurrentPo({ id, label, supplier_id: supplierId, supplier_name: supplierName  }));
    dispatch(setStatus('idle'));
  } catch (err) {
    dispatch(setStatus('error'));
    // Mostrar error global (AppNoticeDialog)
    const errorPayload = err && typeof err === 'object' && 'error' in err ? (err as { error: unknown }).error : err;
    const errorObj = errorPayload && typeof errorPayload === 'object' ? (errorPayload as Record<string, unknown>) : {};
    const status = typeof errorObj.status === 'number' ? errorObj.status : typeof errorObj.code === 'number' ? errorObj.code : null;
    const message = typeof errorObj.message === 'string' ? errorObj.message : 'No fue posible obtener la PO seleccionada';
    const details = (errorObj.details as Record<string, unknown>) || {};
    dispatch(
      setAppNotice({
        message,
        status,
        details: '',
        messageFormatted: '',
        action: 'purchases.po.detail',
        endpointName: 'Purchases.GetPODetail',
        validationErrors: details,
      }),
    );
  }
};
